import { useSettingsStore } from "@/stores/SettingsStore";

/** Track all open popout windows so we can notify them on main window unload */
const openPopouts = new Set<Window>();

function notifyPopoutsOnUnload() {
  openPopouts.forEach((popup) => {
    try {
      if (popup.closed) return;
      // Show a reload prompt in the popout
      const overlay = popup.document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.7);";
      overlay.innerHTML =
        '<div style="background:#1f2937;color:white;padding:24px 32px;border-radius:8px;text-align:center;font-family:system-ui,sans-serif;">' +
        '<p style="margin:0 0 8px;font-size:16px;font-weight:600;">Session Refreshed</p>' +
        '<p style="margin:0;font-size:14px;opacity:0.8;">Please close this window and reopen the widget from the main dashboard</p>' +
        "</div>";
      popup.document.body.appendChild(overlay);
    } catch {
      // Cross-origin or closed — ignore
    }
  });
}

// Register the main window unload handler once
let unloadHandlerRegistered = false;
function ensureUnloadHandler() {
  if (unloadHandlerRegistered) return;
  unloadHandlerRegistered = true;
  window.addEventListener("beforeunload", notifyPopoutsOnUnload);
}

const fontSizeMap: Record<string, string> = {
  small: "14px",
  medium: "16px",
  large: "18px",
  "extra-large": "20px",
  "extra-extra-large": "22px",
};

export interface WindowPosition {
  x: number;
  y: number;
  width?: number;
  height?: number;
  fullscreen?: boolean;
}

interface OpenWindowOptions {
  title?: string;
  position?: WindowPosition;
  onContainerReady: (container: HTMLDivElement, popupWindow: Window) => void;
}

/**
 * Opens a new browser popout window with a specified title, position, and size,
 * and injects a container `<div>` into its body for rendering custom content.
 *
 * The function also copies all stylesheets from the main window to the popout window
 * to ensure consistent styling. Once the container is ready, the provided callback
 * `onContainerReady` is invoked with the container element and the popout window instance.
 *
 * @param options - Configuration options for the popout window.
 * @param options.title - The title of the popout window. Defaults to "Popout Window".
 * @param options.position - The position and size of the popout window. If not provided, defaults are used.
 * @param options.onContainerReady - Callback invoked when the container is ready, receiving the container div and the popout window.
 *
 */
export function openPopoutWindow({
  title = "Popout Window",
  position,
  onContainerReady,
}: OpenWindowOptions) {
  const {
    x = window.outerWidth / 2,
    y = 0,
    height = window.outerHeight,
    width = window.outerWidth / 2,
  } = position || {};

  const fullscreen = position?.fullscreen ? "fullscreen=yes," : "";

  const popup = window.open(
    "",
    title,
    `left=${x},top=${y},height=${height},width=${width},${fullscreen}`
  );

  if (!popup) {
    console.error("Failed to open popout window");
    return;
  }

  // Track this popout and ensure main window unload handler is registered
  openPopouts.add(popup);
  ensureUnloadHandler();
  popup.addEventListener("beforeunload", () => {
    openPopouts.delete(popup);
  });

  popup.document.title = title;
  popup.document.body.style.margin = "0";
  popup.document.body.style.padding = "0";
  popup.document.documentElement.style.height = "100%";

  // Apply current theme to popout window
  const currentTheme = useSettingsStore.getState().theme;
  if (currentTheme === "dark") {
    popup.document.documentElement.classList.add("dark");
  }
  popup.document.documentElement.setAttribute("data-theme", currentTheme);

  // Apply current font size to popout window
  const currentFontSize = useSettingsStore.getState().fontSize;
  popup.document.documentElement.style.fontSize = fontSizeMap[currentFontSize];
  popup.document.documentElement.setAttribute("data-font-size", currentFontSize);

  // Subscribe to settings changes and sync with popout window
  const unsubscribe = useSettingsStore.subscribe((state) => {
    // Theme sync
    if (state.theme === "dark") {
      popup.document.documentElement.classList.add("dark");
    } else {
      popup.document.documentElement.classList.remove("dark");
    }
    popup.document.documentElement.setAttribute("data-theme", state.theme);

    // Font size sync
    popup.document.documentElement.style.fontSize = fontSizeMap[state.fontSize];
    popup.document.documentElement.setAttribute("data-font-size", state.fontSize);
  });

  // Clean up subscription when window closes
  popup.addEventListener("beforeunload", () => {
    unsubscribe();
  });

  const container = popup.document.createElement("div");

  container.className = "h-full w-full bg-white dark:bg-gray-900";
  popup.document.body.appendChild(container);

  // Copy styles from main window
  Array.from(document.styleSheets).forEach((styleSheet) => {
    try {
      if (styleSheet.href) {
        const link = popup.document.createElement("link");
        link.rel = "stylesheet";
        link.href = styleSheet.href;
        popup.document.head.appendChild(link);
      } else if (styleSheet.cssRules) {
        const style = popup.document.createElement("style");
        Array.from(styleSheet.cssRules).forEach((rule) => {
          style.appendChild(popup.document.createTextNode(rule.cssText));
        });
        popup.document.head.appendChild(style);
      }
    } catch (e) {
      console.warn("Could not copy stylesheet:", e);
    }
  });

  onContainerReady(container, popup);
}
